import { supabase } from "@/integrations/supabase/client";

// Activity types supported by the system
export type ActivityType = "app_usage" | "tutor_chat" | "challenge" | "mind_map" | "collaboration";

// Page identifiers for tracking WHERE users are
export type PageLocation =
  | "tutor"
  | "challenges"
  | "ideas"
  | "profile"
  | "classroom"
  | "home"
  | "other";

// Component identifiers for more granular tracking
export type ComponentLocation =
  | "chat"
  | "history"
  | "mindmap"
  | "sidebar"
  | "challenge_list"
  | "challenge_detail"
  | "submission"
  | "other";

// Extended metadata for activity logging
export interface ActivityMetadata {
  chatType?: "ai_tutor";
  source?: "student_app";
  page?: PageLocation;
  component?: ComponentLocation;
  tabVisible?: boolean;
  conversationId?: string;
  challengeId?: string;
  [key: string]: unknown;
}

// LocalStorage keys
const STORAGE_KEY_SESSION = "curio_activity_session";
const STORAGE_KEY_PENDING = "curio_activity_pending";

// Session data stored in localStorage
interface StoredSession {
  userId: string;
  activityType: ActivityType;
  page: PageLocation;
  component: ComponentLocation;
  startTime: number; // timestamp when this session segment started
  accumulatedSeconds: number; // seconds accumulated so far (before current segment)
  lastActiveTime: number; // last time user was active
  isTabVisible: boolean;
}

// Pending activity log to flush to DB
interface PendingActivityLog {
  userId: string;
  activityType: ActivityType;
  duration: number;
  page: PageLocation;
  component: ComponentLocation;
  timestamp: number;
  metadata?: Record<string, unknown>;
}

// Class to track user activity with localStorage persistence
class ActivityTracker {
  private isTracking: boolean = false;
  private activityType: ActivityType = "app_usage";
  private userId: string | null = null;
  private flushInterval: number | null = null;
  private tickInterval: number | null = null;
  private flushIntervalTime: number = 2 * 60 * 1000; // 2 minutes - flush to DB
  private tickIntervalTime: number = 1000; // 1 second - update localStorage
  private idleThreshold: number = 5 * 60 * 1000; // 5 minutes in milliseconds

  // Current state
  private currentPage: PageLocation = "other";
  private currentComponent: ComponentLocation = "other";
  private isTabVisible: boolean = true;
  private customMetadata: Record<string, unknown> = {};

  // Timing
  private segmentStartTime: number = 0;
  private accumulatedSeconds: number = 0;
  private lastActiveTime: number = 0;

  constructor() {
    // Recover any pending logs on initialization
    this.recoverPendingLogs();
  }

  // Set the user ID for tracking
  setUserId(userId: string | null) {
    if (userId !== this.userId) {
      // If switching users, flush current activity
      if (this.userId && this.isTracking) {
        this.flushToStorage();
      }
      this.userId = userId;

      // Try to recover session for new user
      if (userId) {
        this.recoverSession();
      }
    }
  }

  // Get current user ID
  getUserId(): string | null {
    return this.userId;
  }

  // Set the current page location
  setPage(page: PageLocation) {
    if (this.currentPage !== page) {
      // Flush current activity for the old page before changing
      if (this.isTracking && this.isTabVisible) {
        this.flushToStorage();
        this.commitPendingActivity();
      }
      this.currentPage = page;
      // Reset timing for new page
      if (this.isTracking) {
        this.segmentStartTime = Date.now();
        this.accumulatedSeconds = 0;
        this.lastActiveTime = this.segmentStartTime;
        this.saveSessionToStorage();
      }
    }
  }

  // Get current page
  getPage(): PageLocation {
    return this.currentPage;
  }

  // Set the current component location
  setComponent(component: ComponentLocation) {
    this.currentComponent = component;
    if (this.isTracking) {
      this.saveSessionToStorage();
    }
  }

  // Get current component
  getComponent(): ComponentLocation {
    return this.currentComponent;
  }

  // Set tab visibility
  setTabVisible(visible: boolean) {
    const wasHidden = !this.isTabVisible;
    const wasVisible = this.isTabVisible;
    this.isTabVisible = visible;

    if (this.isTracking) {
      if (!visible && wasVisible) {
        // Tab became hidden: save current progress and pause
        this.flushToStorage();
        this.saveSessionToStorage();
      } else if (visible && wasHidden) {
        // Tab became visible: reset timing (don't count time away)
        this.segmentStartTime = Date.now();
        this.lastActiveTime = this.segmentStartTime;
        this.saveSessionToStorage();
      }
    }
  }

  // Get tab visibility
  getTabVisible(): boolean {
    return this.isTabVisible;
  }

  // Set custom metadata
  setCustomMetadata(metadata: Record<string, unknown>) {
    this.customMetadata = { ...this.customMetadata, ...metadata };
  }

  // Clear custom metadata
  clearCustomMetadata() {
    this.customMetadata = {};
  }

  // Start tracking a specific activity
  startTracking(activityType: ActivityType = "app_usage") {
    if (!this.userId) {
      console.warn("Cannot start tracking: No user ID set");
      return;
    }

    if (this.isTracking) {
      // Synchronous cleanup — avoids the race condition where an async
      // stopTracking() resolves later and kills the newly created intervals.
      // Pending data is committed to localStorage and will be sent on the
      // next flush cycle.
      this.flushToStorage();
      this.commitPendingActivity();
      if (this.tickInterval) {
        clearInterval(this.tickInterval);
        this.tickInterval = null;
      }
      if (this.flushInterval) {
        clearInterval(this.flushInterval);
        this.flushInterval = null;
      }
      this.isTracking = false;
    }

    this.activityType = activityType;
    this.segmentStartTime = Date.now();
    this.lastActiveTime = this.segmentStartTime;
    this.accumulatedSeconds = 0;
    this.isTracking = true;

    // Save initial session to storage
    this.saveSessionToStorage();

    // Set up tick interval to update localStorage every second
    if (!this.tickInterval) {
      this.tickInterval = window.setInterval(() => {
        this.tick();
      }, this.tickIntervalTime);
    }

    // Set up flush interval to save to DB periodically
    if (!this.flushInterval) {
      this.flushInterval = window.setInterval(() => {
        this.flushToDatabase();
      }, this.flushIntervalTime);
    }
  }

  // Register user activity to prevent idle detection
  registerActivity() {
    if (!this.isTracking) return;
    this.lastActiveTime = Date.now();
  }

  // Stop tracking and save all activity
  async stopTracking() {
    if (!this.isTracking) return;

    // Flush any remaining activity
    this.flushToStorage();
    await this.flushToDatabase();

    this.isTracking = false;
    this.accumulatedSeconds = 0;

    // Clear intervals
    if (this.tickInterval) {
      clearInterval(this.tickInterval);
      this.tickInterval = null;
    }
    if (this.flushInterval) {
      clearInterval(this.flushInterval);
      this.flushInterval = null;
    }

    // Clear session from storage
    this.clearSessionFromStorage();
  }

  // Change the current activity type
  changeActivityType(activityType: ActivityType) {
    if (this.activityType === activityType) return;

    if (this.isTracking) {
      // Flush current activity before changing type
      this.flushToStorage();
      this.commitPendingActivity();
    }

    this.activityType = activityType;
    this.segmentStartTime = Date.now();
    this.lastActiveTime = this.segmentStartTime;
    this.accumulatedSeconds = 0;

    if (this.isTracking) {
      this.saveSessionToStorage();
    }
  }

  // Tick function - called every second to update localStorage
  private tick() {
    if (!this.isTracking || !this.isTabVisible) return;

    // Check for idle
    const now = Date.now();
    const timeSinceLastActivity = now - this.lastActiveTime;

    if (timeSinceLastActivity > this.idleThreshold) {
      // User is idle - flush what we have and pause
      this.flushToStorage();
      return;
    }

    // Update session in storage
    this.saveSessionToStorage();
  }

  // Calculate current duration for this segment
  private calculateSegmentDuration(): number {
    if (!this.isTracking || !this.isTabVisible) return 0;

    const now = Date.now();
    const timeSinceLastActivity = now - this.lastActiveTime;

    if (timeSinceLastActivity > this.idleThreshold) {
      // User is idle — only count up to lastActiveTime + idleThreshold.
      // If that effective end is before this segment even started, return 0.
      const effectiveEnd = this.lastActiveTime + this.idleThreshold;
      if (effectiveEnd <= this.segmentStartTime) {
        return 0;
      }
      return Math.max(0, Math.floor((effectiveEnd - this.segmentStartTime) / 1000));
    }

    return Math.max(0, Math.floor((now - this.segmentStartTime) / 1000));
  }

  // Flush current segment to accumulated storage (doesn't send to DB yet)
  private flushToStorage() {
    if (!this.isTracking) return;

    const segmentDuration = this.calculateSegmentDuration();
    if (segmentDuration > 0) {
      this.accumulatedSeconds += segmentDuration;
    }

    // Reset segment start but preserve lastActiveTime so idle detection
    // keeps working across flush cycles (only registerActivity / explicit
    // user-driven events should reset the idle timer).
    this.segmentStartTime = Date.now();

    this.saveSessionToStorage();
  }

  // Commit pending activity to the pending queue
  private commitPendingActivity() {
    if (!this.userId || this.accumulatedSeconds <= 0) return;

    const pending: PendingActivityLog = {
      userId: this.userId,
      activityType: this.activityType,
      duration: this.accumulatedSeconds,
      page: this.currentPage,
      component: this.currentComponent,
      timestamp: Date.now(),
      metadata: { ...this.customMetadata },
    };

    this.addToPendingQueue(pending);
    this.accumulatedSeconds = 0;
    this.saveSessionToStorage();
  }

  // Flush accumulated activity to database
  async flushToDatabase() {
    // First, commit any current activity to pending
    this.flushToStorage();
    this.commitPendingActivity();

    // Then flush all pending to database
    await this.flushPendingToDatabase();
  }

  // Flush activity (called externally)
  async flushActivity() {
    await this.flushToDatabase();
  }

  // Save current session to localStorage
  private saveSessionToStorage() {
    if (!this.userId) return;

    const session: StoredSession = {
      userId: this.userId,
      activityType: this.activityType,
      page: this.currentPage,
      component: this.currentComponent,
      startTime: this.segmentStartTime,
      accumulatedSeconds: this.accumulatedSeconds,
      lastActiveTime: this.lastActiveTime,
      isTabVisible: this.isTabVisible,
    };

    try {
      localStorage.setItem(STORAGE_KEY_SESSION, JSON.stringify(session));
    } catch (e) {
      console.error("Failed to save session to localStorage:", e);
    }
  }

  // Clear session from localStorage
  private clearSessionFromStorage() {
    try {
      localStorage.removeItem(STORAGE_KEY_SESSION);
    } catch (e) {
      console.error("Failed to clear session from localStorage:", e);
    }
  }

  // Recover session from localStorage (e.g., after page refresh)
  private recoverSession() {
    try {
      const stored = localStorage.getItem(STORAGE_KEY_SESSION);
      if (!stored) return;

      const session: StoredSession = JSON.parse(stored);

      if (session.userId !== this.userId) {
        this.clearSessionFromStorage();
        return;
      }

      // Recover the unfinished segment that was in progress when the page
      // closed / crashed. This is the time between segmentStartTime and
      // the last known user interaction, which saveSessionToStorage persists
      // every second but flushToStorage only folds into accumulatedSeconds
      // every ~2 minutes.
      const unfinishedSegmentSeconds =
        session.lastActiveTime > session.startTime
          ? Math.floor((session.lastActiveTime - session.startTime) / 1000)
          : 0;
      const totalRecoverableSeconds = session.accumulatedSeconds + unfinishedSegmentSeconds;

      const timeSinceLastActive = Date.now() - session.lastActiveTime;
      if (timeSinceLastActive > this.idleThreshold) {
        // Session is stale — commit whatever was accumulated + unfinished segment
        if (totalRecoverableSeconds > 0) {
          const pending: PendingActivityLog = {
            userId: session.userId,
            activityType: session.activityType,
            duration: totalRecoverableSeconds,
            page: session.page,
            component: session.component,
            timestamp: session.lastActiveTime,
          };
          this.addToPendingQueue(pending);
        }
        this.clearSessionFromStorage();
        return;
      }

      // Session is fresh — recover state and start a new segment
      this.activityType = session.activityType;
      this.currentPage = session.page;
      this.currentComponent = session.component;
      this.accumulatedSeconds = totalRecoverableSeconds;
    } catch (e) {
      console.error("Failed to recover session from localStorage:", e);
      this.clearSessionFromStorage();
    }
  }

  // Add activity to pending queue in localStorage
  private addToPendingQueue(log: PendingActivityLog) {
    try {
      const stored = localStorage.getItem(STORAGE_KEY_PENDING);
      const pending: PendingActivityLog[] = stored ? JSON.parse(stored) : [];
      pending.push(log);
      localStorage.setItem(STORAGE_KEY_PENDING, JSON.stringify(pending));
    } catch (e) {
      console.error("Failed to add to pending queue:", e);
    }
  }

  // Get pending queue from localStorage
  private getPendingQueue(): PendingActivityLog[] {
    try {
      const stored = localStorage.getItem(STORAGE_KEY_PENDING);
      return stored ? JSON.parse(stored) : [];
    } catch (e) {
      console.error("Failed to get pending queue:", e);
      return [];
    }
  }

  // Clear pending queue
  private clearPendingQueue() {
    try {
      localStorage.removeItem(STORAGE_KEY_PENDING);
    } catch (e) {
      console.error("Failed to clear pending queue:", e);
    }
  }

  // Flush pending queue to database
  private async flushPendingToDatabase() {
    const pending = this.getPendingQueue();
    if (pending.length === 0) return;

    // Clear queue first (optimistic)
    this.clearPendingQueue();

    // Try to send each log
    const failed: PendingActivityLog[] = [];

    for (const log of pending) {
      const success = await this.sendActivityToDatabase(log);
      if (!success) {
        failed.push(log);
      }
    }

    // Re-add failed logs to queue
    if (failed.length > 0) {
      try {
        const current = this.getPendingQueue();
        localStorage.setItem(STORAGE_KEY_PENDING, JSON.stringify([...failed, ...current]));
      } catch (e) {
        console.error("Failed to re-add failed logs:", e);
      }
    }
  }

  // Recover any pending logs on initialization
  private async recoverPendingLogs() {
    // Wait a bit for auth to initialize
    setTimeout(() => {
      this.flushPendingToDatabase();
    }, 2000);
  }

  // Send a single activity log to the database
  private async sendActivityToDatabase(log: PendingActivityLog): Promise<boolean> {
    try {
      const { data: profile, error: profileError } = await supabase
        .from("profiles")
        .select("is_test_student, role")
        .eq("id", log.userId)
        .maybeSingle();

      if (profileError) {
        // Transient: returning false re-queues the log for the next flush.
        // Logged as warn so intermittent network blips don't surface as errors
        // in crash reports / bug-report log feeds.
        console.warn(
          "[activityTracker.sendActivityToDatabase] profile fetch failed, will retry:",
          profileError
        );
        return false;
      }

      if (!profile || profile.role !== "student" || profile.is_test_student) {
        return true;
      }

      // Build metadata
      const metadata: ActivityMetadata = {
        ...(log.activityType === "tutor_chat" && { chatType: "ai_tutor" }),
        ...(log.activityType === "challenge" && { source: "student_app" }),
        page: log.page,
        component: log.component,
        tabVisible: true,
        ...log.metadata,
      };

      if (process.env.NODE_ENV === "development") {
        console.log(`Activity: ${log.activityType}, ${log.duration}s, page: ${log.page}`);
      }

      const { error } = await supabase.from("student_activity_logs").insert({
        student_id: log.userId,
        activity_type: log.activityType,
        duration: log.duration,
        metadata: metadata as { [key: string]: string | number | boolean | null | undefined },
      });

      if (error) {
        // Transient: requeued for next flush; warn keeps crash logs clean.
        console.warn("[activityTracker.sendActivityToDatabase] insert failed, will retry:", error);
        return false;
      }

      return true;
    } catch (err) {
      // Network-level failure (e.g. `TypeError: Failed to fetch` on flaky Wi-Fi).
      // Requeued for next flush; warn so intermittent connectivity issues don't
      // surface as errors in crash reports / bug-report log feeds.
      console.warn("[activityTracker.sendActivityToDatabase] network failure, will retry:", err);
      return false;
    }
  }

  // Check if tracking is active
  isActive(): boolean {
    return this.isTracking;
  }

  // Get current accumulated seconds (for debugging/display)
  getCurrentSeconds(): number {
    return this.accumulatedSeconds + this.calculateSegmentDuration();
  }

  // Clean up resources
  destroy() {
    this.stopTracking();
  }
}

// Create a singleton instance
export const activityTracker = new ActivityTracker();
